import { cabinetDisplayName } from "@/lib/wording";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { currentCountry } from "@/jurisdictions/country";
import { FR_TEAM_UI_ENABLED } from "@/jurisdictions/features";
import { moduleAllowed } from "@/jurisdictions/workspaceNavigation";
import { api, isCabinetAdmin, isSuperAdmin, ROLE_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PasswordDialog } from "@/components/PasswordDialog";
import { MySessionsDialog } from "@/components/MySessionsDialog";
import { Button } from "@/components/ui/button";
import {
  Plus, House, Lightning, Scales, BookOpen, Briefcase, Buildings, FileText,
  ClockCounterClockwise, Vault, Receipt, Gear, CaretDoubleLeft, CaretDoubleRight,
  X, Trash, ChatCircleDots, SignOut, CalendarBlank, UsersThree, Bell, Robot,
} from "@phosphor-icons/react";

export function AppSidebar({
  mobileOpen,
  onCloseMobile,
  showConversations = false,
  convos = [],
  convId = null,
  activeClientId = null,
  onNewConvo,
  onOpenConvo,
  onRemoveConvo,
  onOpenSkills,
  onOpenRevisions,
  onOpenJournal,
}) {
  const { cabinet, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsedState] = useState(() => localStorage.getItem("fidalis_sidebar_collapsed") === "1");
  const setCollapsed = (v) => { setCollapsedState(v); localStorage.setItem("fidalis_sidebar_collapsed", v ? "1" : "0"); };
  const france = currentCountry() === "FR";
  const searchParams = new URLSearchParams(location.search);
  const clientId = activeClientId || searchParams.get("client");
  const documentsOpen = france && location.pathname === "/app/clients" && searchParams.get("tab") === "documents";
  const documentsPath = `/app/clients?${new URLSearchParams({ tab: "documents", ...(clientId ? { client: clientId } : {}) })}`;
  const brand = cabinetDisplayName(cabinet);
  const isPartner = isCabinetAdmin(user);
  const isSuper = isSuperAdmin(user);
  const canReadDeadlines = france ? moduleAllowed(user, cabinet, "echeances") : isPartner;
  const switchCabinet = async (cid) => {
    if (!cid || cid === user?.cabinet_id) return;
    try { const { data } = await api.post("/auth/switch-cabinet", { cabinet_id: cid });
      localStorage.setItem("fidalis_token", data.token); window.location.href = "/app"; } catch { /* ignore */ }
  };

  const [deadlineCount, setDeadlineCount] = useState(0);
  useEffect(() => {
    if (!canReadDeadlines) return;
    let alive = true;
    const load = () => api.get("/deadlines/upcoming?days=7")
      .then(({ data }) => { if (alive) setDeadlineCount((data || []).length); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [canReadDeadlines, location.pathname, france]);

  const [failedEmails, setFailedEmails] = useState(0);
  useEffect(() => {
    if (!isPartner || france) return;
    let alive = true;
    const load = () => api.get("/email-logs/failed-count")
      .then(({ data }) => { if (alive) setFailedEmails(data.count || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [isPartner, location.pathname, france]);

  const rawNavGroups = [
    {
      title: null,
      items: [
        { icon: Plus, label: "Nouvelle conversation", onClick: onNewConvo, testid: "new-conversation-button", primary: true },
        { icon: House, label: "Accueil", onClick: onNewConvo, testid: "nav-home", path: "/app" },
        ...(france && FR_TEAM_UI_ENABLED ? [{ icon: Robot, label: "Équipe spécialisée", onClick: () => navigate("/app/agents"), testid: "nav-agents", path: "/app/agents" }] : []),
        { icon: Lightning, label: "Raccourcis", onClick: onOpenSkills, testid: "nav-skills" },
        ...(france && isSuper ? [{ icon: Buildings, label: "Administration", onClick: () => navigate("/app/admin"), testid: "nav-admin", path: "/app/admin" }] : []),
      ],
    },
    {
      title: "Documentation",
      items: france ? [
        { icon: Scales, label: "Documentation juridique", onClick: () => navigate("/app/recherche-juridique"), testid: "nav-recherche-juridique", path: "/app/recherche-juridique" },
        { icon: BookOpen, label: "Conventions collectives", onClick: () => navigate("/app/referentiel-travail"), testid: "nav-referentiel-travail", path: "/app/referentiel-travail" },
      ] : [
        { icon: Scales, label: "Recherche juridique", onClick: () => navigate("/app/recherche-juridique"), testid: "nav-recherche-juridique", path: "/app/recherche-juridique" },
        { icon: Bell, label: "Veille réglementaire", onClick: () => navigate("/app/veille"), testid: "nav-veille", path: "/app/veille" },
        { icon: BookOpen, label: "Référentiel CGI", onClick: () => navigate("/app/referentiel-cgi"), testid: "nav-referentiel-cgi", path: "/app/referentiel-cgi" },
        { icon: Briefcase, label: "Code du Travail", onClick: () => navigate("/app/referentiel-travail"), testid: "nav-referentiel-travail", path: "/app/referentiel-travail" },
        { icon: Buildings, label: "Loi SA (17-95)", onClick: () => navigate("/app/referentiel-sa"), testid: "nav-referentiel-sa", path: "/app/referentiel-sa" },
        { icon: Buildings, label: "Loi SARL (5-96)", onClick: () => navigate("/app/referentiel-sarl"), testid: "nav-referentiel-sarl", path: "/app/referentiel-sarl" },
        { icon: FileText, label: "Doctrine DGI", onClick: () => navigate("/app/referentiel-dgi"), testid: "nav-referentiel-dgi", path: "/app/referentiel-dgi" },
      ],
    },
    ...(isPartner || france ? [{
      title: "Portefeuille",
      items: [
        { icon: UsersThree, label: "Clients", onClick: () => navigate("/app/clients"), testid: "nav-clients", path: "/app/clients", active: location.pathname === "/app/clients" && !documentsOpen },
        ...(france ? [{ icon: FileText, label: "Pièces du dossier", onClick: () => navigate(documentsPath), testid: "nav-documents", active: documentsOpen }] : []),
        { icon: Briefcase, label: "Missions", onClick: () => navigate("/app/missions"), testid: "nav-missions", path: "/app/missions" },
        { icon: CalendarBlank, label: "Échéances", onClick: () => navigate("/app/calendrier"), testid: "nav-calendrier", path: "/app/calendrier", badge: deadlineCount },
        { icon: BookOpen, label: france ? "Contrôle FEC" : "Grands livres", onClick: () => navigate("/app/grands-livres"), testid: "nav-grands-livres", path: "/app/grands-livres" },
      ],
    }, {
      title: "Cabinet",
      items: france ? [
        { icon: Receipt, label: "Simulation IS", onClick: () => navigate("/app/clients?tab=is"), testid: "nav-is" },
        { icon: Gear, label: "Cabinet & équipe", onClick: () => navigate("/app/parametres"), testid: "open-settings", path: "/app/parametres" },
        { icon: Lightning, label: "Services du cabinet", onClick: () => navigate("/app/fonctions"), testid: "nav-coverage", path: "/app/fonctions" },
      ] : [
        { icon: ClockCounterClockwise, label: "Historique paie", onClick: () => navigate("/app/paie-historique"), testid: "nav-paie-history", path: "/app/paie-historique" },
        { icon: Vault, label: "Coffre RH", onClick: () => navigate("/app/coffre-rh"), testid: "nav-coffre-rh", path: "/app/coffre-rh" },
        { icon: Receipt, label: "Révisions", onClick: onOpenRevisions, testid: "nav-revisions" },
        { icon: Gear, label: "Personnalisation & API", onClick: () => navigate("/app/parametres"), testid: "open-settings", path: "/app/parametres", alert: failedEmails },
      ],
    }] : []),
    ...(isSuper && !france ? [{
      title: "Plateforme",
      items: [
        { icon: Buildings, label: "Administration", onClick: () => navigate("/app/admin"), testid: "nav-admin", path: "/app/admin" },
      ],
    }] : []),
  ];
  const navModules = { "nav-clients": "clients", "nav-documents": "clients", "nav-missions": "missions", "nav-calendrier": "echeances", "nav-grands-livres": "clients", "nav-is": "liasse" };
  const navGroups = rawNavGroups.map((group) => ({ ...group, items: group.items.filter((item) => {
    if (!france) return true;
    if (item.testid === "open-settings") return isPartner;
    const module = navModules[item.testid];
    return !module || moduleAllowed(user, cabinet, module);
  }) })).filter((group) => group.items.length);

  const inner = (
    <div className="flex flex-col h-full min-h-0 w-full min-w-0 bg-card">
      {/* En-tête fixe */}
      <div className={`shrink-0 flex items-center gap-2.5 border-b border-border ${collapsed ? "px-2 py-3.5 justify-center" : "px-4 py-3.5"}`}>
        {cabinet?.logo_url ? <img src={cabinet.logo_url} alt="logo" className="h-8 w-8 rounded-lg object-cover shrink-0" /> : <Logo className="h-8 w-8 shrink-0" />}
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-sm truncate leading-tight">{brand}</div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground truncate">Espace de travail</div>
          </div>
        )}
        {!collapsed && (
          <button onClick={() => setCollapsed(true)} data-testid="collapse-sidebar" className="hidden md:inline-flex text-muted-foreground hover:text-foreground transition-colors" title="Réduire">
            <CaretDoubleLeft size={16} />
          </button>
        )}
        <button onClick={onCloseMobile} data-testid="sidebar-close" className="md:hidden text-muted-foreground hover:text-foreground transition-colors" title="Fermer">
          <X size={18} />
        </button>
      </div>

      {!collapsed && (user?.cabinets?.length > 1) && (
        <div className="shrink-0 px-3 pt-2" data-testid="cabinet-switcher">
          <select value={user.cabinet_id || ""} onChange={(e) => switchCabinet(e.target.value)} data-testid="cabinet-switcher-select"
            className="w-full h-8 text-xs rounded-md border border-border bg-background px-2">
            {user.cabinets.map((c) => <option key={c.cabinet_id} value={c.cabinet_id}>{c.nom} · {c.role}</option>)}
          </select>
        </div>
      )}

      {collapsed && (
        <button onClick={() => setCollapsed(false)} data-testid="expand-sidebar" className="shrink-0 hidden md:flex mx-auto my-2 text-muted-foreground hover:text-foreground transition-colors" title="Agrandir">
          <CaretDoubleRight size={16} />
        </button>
      )}

      {/* Zone centrale unique scrollable : navigation + conversations */}
      <nav className={`flex-1 min-h-0 overflow-y-auto overscroll-contain py-3 space-y-4 ${collapsed ? "px-2" : "px-3"}`} data-testid="sidebar-scroll">
        {navGroups.map((group, gi) => (
          <div key={gi} className="space-y-1">
            {group.title && !collapsed && (
              <div className="px-3 pb-1 text-[10px] font-mono font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">{group.title}</div>
            )}
            {group.title && collapsed && gi > 0 && <div className="mx-2 mb-1 border-t border-border/60" />}
            {group.items.map((it) => {
              const active = it.active ?? (it.path && location.pathname === it.path);
              return (
                <button key={it.testid} onClick={it.onClick} data-testid={it.testid} title={it.label} aria-current={active ? "page" : undefined}
                  className={`w-full flex items-center rounded-lg text-sm transition-all duration-150 ${collapsed ? "justify-center h-10 relative" : "gap-2.5 px-3 py-2"} ${
                    it.primary
                      ? "bg-primary text-primary-foreground hover:opacity-90 shadow-sm"
                      : active
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}>
                  <it.icon size={17} weight={it.primary || active ? "bold" : "regular"} className="shrink-0" />
                  {!collapsed && <span className="truncate flex-1 text-left">{it.label}</span>}
                  {it.badge > 0 && !collapsed && (
                    <span data-testid="sidebar-deadline-badge" className="shrink-0 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[11px] font-bold leading-none">
                      {it.badge > 99 ? "99+" : it.badge}
                    </span>
                  )}
                  {it.badge > 0 && collapsed && (
                    <span data-testid="sidebar-deadline-badge" className="absolute top-1 right-1 h-4 min-w-[16px] px-1 inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-[9px] font-bold leading-none">
                      {it.badge > 9 ? "9+" : it.badge}
                    </span>
                  )}
                  {it.alert > 0 && !collapsed && (
                    <span data-testid="sidebar-email-alert" title={`${it.alert} e-mail(s) en échec`} className="shrink-0 min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-destructive text-white text-[11px] font-bold leading-none">
                      {it.alert > 99 ? "99+" : it.alert}
                    </span>
                  )}
                  {it.alert > 0 && collapsed && (
                    <span data-testid="sidebar-email-alert" title={`${it.alert} e-mail(s) en échec`} className="absolute bottom-1 right-1 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-card" />
                  )}
                </button>
              );
            })}
            {gi === 0 && !france && <NotificationBell collapsed={collapsed} onOpenJournal={onOpenJournal} />}
          </div>
        ))}

        {/* Discussions récentes */}
        {showConversations && !collapsed && user?.role !== "client" && (
          <div className="space-y-0.5 pt-1">
            <div className="px-3 pb-1 text-[10px] font-mono font-semibold uppercase tracking-[0.14em] text-muted-foreground/70">Discussions récentes</div>
            {convos.map((c) => (
              <div key={c.id} onClick={() => onOpenConvo(c.id)} data-testid={`conversation-${c.id}`}
                className={`group flex items-center gap-2 px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors ${c.id === convId ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted/50"}`}>
                <ChatCircleDots size={15} weight={c.id === convId ? "fill" : "regular"} className={`shrink-0 ${c.id === convId ? "text-primary" : ""}`} />
                <span className="truncate flex-1 min-w-0">{c.titre || "Conversation"}</span>
                <button onClick={(e) => onRemoveConvo(c.id, e)} data-testid={`delete-conversation-${c.id}`} aria-label="Supprimer" className="opacity-0 group-hover:opacity-70 hover:!opacity-100 text-muted-foreground hover:text-destructive transition-opacity"><Trash size={13} /></button>
              </div>
            ))}
            {convos.length === 0 && <p className="text-xs text-muted-foreground px-3 py-2">Aucune conversation.</p>}
          </div>
        )}
      </nav>

      {/* Bloc profil + déconnexion — fixe en bas, toujours visible */}
      <div className={`shrink-0 border-t border-border py-3 ${collapsed ? "px-2" : "px-3"}`}>
        {!collapsed ? (
          <>
            <div className="flex items-center gap-2.5 px-2 py-1.5">
              <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold uppercase">
                {(user?.name || user?.email || "?").slice(0, 1)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                {france && <div data-testid="account-role" className="mt-1 text-xs font-semibold text-primary truncate">
                  {isSuper ? "Super administrateur" : ROLE_LABEL[user?.role] || cabinet?.custom_roles?.find((role) => role.key === user?.role)?.label || user?.role}
                </div>}
              </div>
            </div>
            <div className="mt-1.5 space-y-1.5">
              <div className="flex items-center gap-1">
                <ThemeToggle />
                <PasswordDialog />
              </div>
              <MySessionsDialog />
              <Button variant="ghost" size="sm" className="w-full min-w-0 justify-start text-muted-foreground hover:text-destructive hover:bg-destructive/10" data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }}>
                <SignOut size={16} className="mr-2 shrink-0" /> Déconnexion
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <ThemeToggle />
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button" title="Déconnexion" className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"><SignOut size={16} /></button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <aside className={`hidden md:flex shrink-0 border-r border-border bg-card relative z-20 overflow-hidden transition-all duration-200 ${collapsed ? "w-[68px]" : "w-64"}`}>{inner}</aside>
      {mobileOpen && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden animate-fade-in" onClick={onCloseMobile} data-testid="sidebar-overlay" />
          <aside className="fixed inset-y-0 left-0 w-[84%] max-w-xs z-50 border-r border-border bg-card md:hidden shadow-2xl animate-slide-in-left">{inner}</aside>
        </>
      )}
    </>
  );
}
